import { ActivityIndicator, ScrollView } from "react-native";
import { Stack, useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { PressableScale } from "pressto";
import { View, Text, Card, Button } from "@src/components";
import { Icon } from "@src/components/Icon";
import { AlarmStateBadge } from "@src/features/alarms/components/AlarmStateBadge";
import { DiagonalStripeSquare } from "@src/features/schedules/components/DiagonalStripeSquare";
import { useSiteCameraKeys } from "@src/features/cameras/hooks/useSiteCameraKeys";
import { useExceptionSuggestions } from "@src/services/api/hooks";
import { capitalize } from "@src/utils/capitalize";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import type { AlarmExceptionSuggestion } from "@src/services/api/types";
import type { FC } from "react";
import { makeDateTimeRecord } from "@visioni/schedules";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { formatSuggestionDate, buildPeriodFromSuggestion } from "@src/features/alarms/utils/exception-format";

export default function CreateExceptionScreen() {
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();
  const router = useRouter();
  const { cameraKeys, selectedSiteId } = useSiteCameraKeys();
  const { headerLeft } = useHeaderBackButton();
  const firstCameraKey = cameraKeys[0];
  const effectiveSiteId = selectedSiteId ?? "";

  const suggestionsQuery = useExceptionSuggestions(effectiveSiteId, firstCameraKey);
  const suggestions = suggestionsQuery.data ?? [];
  const isLoading = suggestionsQuery.isLoading;

  const navigateToEditException = (exception: { reason: string; action: string; period: unknown }) => {
    router.push({
      pathname: "/alarm-settings/edit-exception",
      params: {
        exception: JSON.stringify(exception),
        includedCameraKeys: JSON.stringify([]),
        timezone: "UTC",
        isNew: "true",
      },
    });
  };

  const handleSelectSuggestion = (suggestion: AlarmExceptionSuggestion) => {
    navigateToEditException({
      reason: suggestion.reason,
      action: suggestion.action,
      period: buildPeriodFromSuggestion(suggestion),
    });
  };

  const handleCreateNew = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const day = now.getDate();

    const exception = {
      reason: "",
      action: "armed",
      period: {
        start: makeDateTimeRecord(year, month, day, 0, 0),
        end: makeDateTimeRecord(year, month, day, 24, 0),
      },
    };

    router.push({
      pathname: "/alarm-settings/edit-exception-details",
      params: {
        exception: JSON.stringify(exception),
        isNew: "true",
        allCameraKeys: JSON.stringify(cameraKeys),
      },
    });
  };

  return (
    <>
      <Stack.Screen options={{ title: "New exception", headerLeft }} />
      <ScrollView
        style={{ flex: 1, backgroundColor: theme.colors.background }}
        contentContainerStyle={{
          paddingHorizontal: theme.spacing["3xl"],
          paddingTop: theme.spacing["4xl"],
          paddingBottom: bottom + theme.spacing["6xl"],
          flexGrow: 1,
        }}
        showsVerticalScrollIndicator={false}
      >
        {isLoading && (
          <View mt="7xl" alignItems="center">
            <ActivityIndicator color={theme.colors.text} />
          </View>
        )}

        {!isLoading && (
          <View gap="3xl">
            {suggestions.length > 0 && (
              <View>
                <Text textStyle="xs/medium" color="textMuted" mb="lg">
                  Suggested
                </Text>
                <View gap="lg">
                  {suggestions.map((suggestion) => (
                    <SuggestionCard
                      key={suggestion.id}
                      suggestion={suggestion}
                      onPress={() => handleSelectSuggestion(suggestion)}
                    />
                  ))}
                </View>
              </View>
            )}

            <Button
              label="Create new"
              variant="primary"
              variantColor="blue"
              size="lg"
              onPress={handleCreateNew}
              iconRight={<Icon name="plus" color={theme.colors.white.default} size={20} />}
            />
          </View>
        )}
      </ScrollView>
    </>
  );
}

interface SuggestionCardProps {
  suggestion: AlarmExceptionSuggestion;
  onPress: () => void;
}

const SuggestionCard: FC<SuggestionCardProps> = ({ suggestion, onPress }) => {
  const { theme } = useUnistyles();
  const { date, time } = formatSuggestionDate(suggestion);

  return (
    <PressableScale onPress={onPress}>
      <Card>
        <View flexDirection="row" justifyContent="space-between" gap="md">
          <View flex={1}>
            <View flexDirection="row" alignItems="center" gap="md" mb="2xl">
              <View style={{ overflow: "hidden" }}>
                <DiagonalStripeSquare state={suggestion.action as "armed" | "disarmed"} size={20} />
              </View>
              <Text textStyle="sm/medium" numberOfLines={2} ellipsizeMode="tail" style={{ flexShrink: 1 }}>
                {suggestion.reason}
              </Text>
              <AlarmStateBadge state={suggestion.action} />
            </View>
            <View flexDirection="row" alignItems="center" gap="xs">
              <Icon name="calendarOutline" color={theme.colors.textMuted} size={16} />
              <Text textStyle="xs/regular" color="textMuted">
                {date}
              </Text>
              {time && (
                <>
                  <Text textStyle="xs/regular" color="textMuted">
                    •
                  </Text>
                  <Icon name="clockOutline" color={theme.colors.textMuted} size={16} />
                  <Text textStyle="xs/regular" color="textMuted">
                    {capitalize(time)}
                  </Text>
                </>
              )}
            </View>
          </View>
          <Icon name="chevronRight" color={theme.colors.textMuted} size={20} />
        </View>
      </Card>
    </PressableScale>
  );
};
